import { useEffect, useState } from "react";
import { KpiBadgeLayer } from "./KpiBadgeLayer";
import { buildAisleBadges, type AisleBadge } from "./kpiBadge";
import {
  getInventoryDivergences, getStructureOccupancy, getStructureVolumes,
  listInventoryRuns, selectedPaintableRuns,
} from "../api/rest";
import { useUIStore } from "../stores/uiStore";

interface Props {
  siteId: string;
  warehouseId: string;
}

/**
 * ADR 193: data wrapper for the aisle KPI badges. Same lifecycle contract as
 * `OccupancyLayerContainer` — keyed per site:warehouse by `Scene.tsx`, one
 * fetch per mount/`sceneDataVersion` bump (runs are batch imports; nothing to
 * poll). Values ride the run's `structure/occupancy` rollup (the report
 * aggregate), anchors the run-independent `structure/volumes`.
 */
export function KpiBadgeLayerContainer({ siteId, warehouseId }: Props) {
  const [badges, setBadges] = useState<AisleBadge[] | null>(null);
  const dataVersion   = useUIStore((s) => s.sceneDataVersion);
  const showCoverage  = useUIStore((s) => s.layers.kpiCoverage);
  const showOccupancy = useUIStore((s) => s.layers.kpiOccupancy);
  const showVertical  = useUIStore((s) => s.layers.kpiVertical);
  const showAccuracy  = useUIStore((s) => s.layers.kpiAccuracy);
  const runSelection  = useUIStore((s) => s.runSelection);

  useEffect(() => {
    let cancelled = false;
    listInventoryRuns(siteId)
      .then((runs) => {
        // ADR 198: badges follow the run picker's PRIMARY run (newest selected)
        // — the rollup and divergence reads are per-run, not mergeable.
        const latest = selectedPaintableRuns(runs, warehouseId, runSelection)[0];
        if (!latest) return null;
        return Promise.all([
          getStructureOccupancy(siteId, warehouseId, latest.id),
          getStructureVolumes(siteId, warehouseId),
          // No WMS snapshot yet → rows [] and the chip shows "—"; a failure on
          // this one fetch must not take the coverage/occupancy chips down.
          getInventoryDivergences(siteId, latest.id).catch(() => ({ rows: [] })),
        ]).then(([occ, vols, div]) => buildAisleBadges(vols.volumes, occ.nodes, div.rows));
      })
      .then((b) => { if (!cancelled) setBadges(b); })
      .catch(() => { if (!cancelled) setBadges(null); });
    return () => { cancelled = true; };
  }, [siteId, warehouseId, dataVersion, runSelection]);

  if (!badges || badges.length === 0) return null;
  return (
    <KpiBadgeLayer
      badges={badges}
      showCoverage={showCoverage}
      showOccupancy={showOccupancy}
      showVertical={showVertical}
      showAccuracy={showAccuracy}
    />
  );
}
